import { Link } from 'react-router-dom';
import GuideLayout from '../../components/guides/GuideLayout';
import GuideCTA from '../../components/guides/GuideCTA';
import { GuideSection, GuideFaqItem, GuideChecklist, GuideNote, SourceLink } from '../../components/guides/GuideParts';

const SLUG = 'what-to-do-if-your-records-are-wrong-or-unkind';

export const RecordsWrongOrUnkind = () => (
  <GuideLayout slug={SLUG}>
    <GuideSection>
      <p>
        Somewhere in your file, a professional you may barely remember wrote something about you
        that stops you cold. Maybe it says you hit the family dog, or hit your foster carer, and
        you know that never happened. Maybe it is technically true but written in a way that
        makes you sound like a problem to be managed, not a child having a hard time. Either way,
        it can feel like reading a stranger&apos;s account of your own life, one you never got to
        argue with at the time, and now have to sit with as if it is simply fact.
      </p>
      <p>
        It is not something you have to just accept. This guide covers what you can actually do —
        the difference between something being wrong and something just feeling wrong, your legal
        right to have inaccurate information corrected, what happens when an organisation says no,
        and where a formal complaint can do more than a data request ever could.
      </p>
    </GuideSection>

    <GuideNote>
      <p>
        <strong>You do not have to read all of this in one sitting.</strong> It is fine to close
        the document, come back tomorrow, or read the rest with someone you trust next to you. A
        record does not stop being yours because you needed a break from it, and nothing here
        needs to be dealt with today.
      </p>
    </GuideNote>

    <GuideSection eyebrow="First, work out which kind of wrong it is" title="Is it wrong, or does it just feel wrong?">
      <p>
        These need different responses, so it is worth being clear with yourself about which one
        you are looking at before you write to anyone. The Information Commissioner&apos;s Office
        (ICO), the UK&apos;s data protection regulator, draws roughly the same three-way line:
      </p>
      <GuideChecklist
        items={[
          <>
            <strong className="text-ink">A stated fact that is simply wrong</strong> — a specific,
            checkable thing that did not happen, or happened differently: a date, an event, an
            incident attributed to the wrong person. This is squarely what the law calls
            "inaccurate personal data," and you have a direct right to have it corrected.
          </>,
          <>
            <strong className="text-ink">A recorded opinion or a one-sided account</strong> — "the
            foster carer reported that…", a social worker&apos;s professional judgement, a note of
            what someone else said happened. The ICO is explicit that{' '}
            <SourceLink href="https://ico.org.uk/for-the-public/your-right-to-get-your-data-corrected/">
              opinions are, by nature, subjective
            </SourceLink>{' '}
            — as long as a record is clear that it is recording an opinion, and whose, it can be
            hard to argue it is "inaccurate" even if you completely disagree with it. That does
            not mean you have no options, just that correction is not the only, or even the best,
            tool for this one.
          </>,
          <>
            <strong className="text-ink">Something accurate, written without care.</strong> Cold,
            clinical, or outdated language that technically describes what happened but reads as
            harsh or dehumanising. This is not a data accuracy problem — the ICO&apos;s own current
            standards for organisations say records should use{' '}
            <SourceLink href="https://ico.org.uk/about-the-ico/campaigns/better-records-together/our-care-records-standards/creating-care-records/">
              "respectful, clear, and understandable language"
            </SourceLink>
            , so this is a fair thing to raise, just through a different route than "please correct
            this."
          </>,
        ]}
      />
    </GuideSection>

    <GuideSection eyebrow="Working through it" title="The dog, or the foster carer, example">
      <p>
        Take the example most people ask us about: a note saying you hit the family dog, or hit
        your foster carer. The words on the page might look the same in each version below, but
        what you can do about it is different.
      </p>
      <GuideChecklist
        items={[
          <>
            <strong className="text-ink">If it is written as plain, undisputed fact</strong> —
            "[Name] hit the dog" or "[Name] hit their foster carer," stated flatly, with nothing
            to say it came from anyone&apos;s account — and it did not happen, this is a factual
            inaccuracy. You have a direct right to challenge it.
          </>,
          <>
            <strong className="text-ink">If it is written as what was reported</strong> — "the
            foster carer told the social worker that…" — the record may be an accurate account of
            what was said, even if what was said was not true. You cannot force the record of "a
            report was made" to be deleted, since that report genuinely happened. What you{' '}
            <em>can</em> do is dispute the underlying claim, ask for your own account of events to
            be added, and ask why no one appears to have recorded your side at the time — which is
            a fair and pointed question in its own right.
          </>,
          <>
            <strong className="text-ink">If it shaped a decision about you</strong> — used to
            justify ending a placement, or repeated in a later assessment as settled fact — this is
            worth pursuing harder than a passing remark would be, including through a formal
            complaint (see below), because the stakes are no longer just "this sentence is wrong,"
            they are "this affected what happened to me."
          </>,
        ]}
      />
    </GuideSection>

    <GuideSection eyebrow="Your legal right" title="Asking for it to be corrected">
      <p>
        Under Article 16 of the{' '}
        <SourceLink href="https://www.legislation.gov.uk/eur/2016/679/article/16">UK GDPR</SourceLink>
        , you can challenge the accuracy of personal data an organisation holds about you and ask
        for it to be corrected, deleted, or — if it is incomplete rather than wrong — completed
        with more detail. This is called the{' '}
        <SourceLink href="https://ico.org.uk/for-the-public/your-right-to-get-your-data-corrected/">
          right to rectification
        </SourceLink>
        , and it is a separate right from a Subject Access Request: a SAR gets you a copy of what
        they hold, rectification is what you use once you have read it and found something wrong.
      </p>
      <GuideChecklist
        items={[
          'State clearly what you believe is inaccurate — quote the exact sentence if you can, not just the general area of the file.',
          'Explain what you believe actually happened, or what the record should say instead.',
          'Give any evidence you have, if you have it — but you are not required to prove your version beyond doubt to be entitled to raise it.',
          'Put it in writing, even if you first raise it verbally or by phone, so there is a clear record of when you challenged it and what you said.',
        ]}
      />
      <p>
        The organisation then has to actually look into it — the ICO says it{' '}
        <strong className="text-ink">
          "should take reasonable steps to investigate whether the data is accurate"
        </strong>{' '}
        — and respond within one calendar month, extendable by up to two further months for a
        complex request, the same timescale as a SAR. It must then either correct the record and
        tell you it has done so, or explain clearly why it believes the record is accurate and is
        declining to change it.
      </p>
    </GuideSection>

    <GuideCTA
      heading="We flag records like this before you open them"
      body="Precious Data shows a short, plain warning ahead of a record that mentions something sensitive, with the option to see support organisations you can call — so you get to choose when, and how, you read something difficult."
    />

    <GuideSection eyebrow="If they say no" title="A refusal still gets you something">
      <p>
        Sometimes an organisation will decline to change the record — most often for the "reported
        opinion" kind of entry described above, where they are standing by it as an accurate note
        of what was said or believed at the time. That is not nothing, though, and it is not the
        end of the road.
      </p>
      <GuideChecklist
        items={[
          <>
            <strong className="text-ink">Your challenge gets recorded either way.</strong> The ICO
            says that if an organisation refuses to correct data,{' '}
            <strong className="text-ink">
              "as a matter of good practice it should record that you have challenged the data&apos;s
              accuracy and why."
            </strong>{' '}
            That means anyone reading your file after you raised this — including you, years from
            now — will see that this was disputed, not just the original, unchallenged claim
            sitting there as if you agreed with it.
          </>,
          <>
            <strong className="text-ink">If it was shared with anyone else, that has to follow
            it.</strong> Where the organisation has disclosed the disputed information to another
            body — another team, another organisation entirely — and you ask, it must tell you who
            received it, and if it does later correct the record, it must contact those recipients
            too, unless that would take disproportionate effort. A false claim rarely stays in one
            file; this is how a correction can follow it.
          </>,
          <>
            You can complain to the organisation directly, and if you are still unhappy, escalate
            to the ICO — see our guide on{' '}
            <Link to="/guides/what-to-do-if-an-organisation-misses-the-deadline" className="text-brand underline hover:opacity-90">
              what to do if an organisation won&apos;t engage
            </Link>{' '}
            for how a complaint to the regulator works in practice.
          </>,
        ]}
      />
    </GuideSection>

    <GuideSection eyebrow="A different route" title="When a complaint does more than a data request">
      <p>
        A data protection rectification request is about accuracy. It is not designed to ask "was
        this a fair or reasonable thing for a professional to have written," and for the
        "reported opinion" scenario above, it will often hit its limit quickly. For that, English
        councils run a separate, formal route: the{' '}
        <SourceLink href="https://www.lgo.org.uk/make-a-complaint/fact-sheets/social-care/social-care-for-children/the-children-act-1989-complaints-procedure">
          children&apos;s statutory complaints procedure
        </SourceLink>{' '}
        under the Children Act 1989, which covers complaints about a council&apos;s services to
        children in need, in care, or in fostering, adoption or special guardianship arrangements
        — including from adults complaining about services they received as children. It has
        three stages: a first response from the team involved, an independent investigation with
        oversight from someone outside that team, and, if you are still unhappy, a review panel of
        independent people.
      </p>
      <GuideNote>
        <p>
          Councils can decline a complaint about something more than a year old, though the
          guidance says they should not apply this rigidly, and can still choose to look into it
          — worth raising even if a lot of time has passed, rather than assuming it is automatically
          too late. This route does not cover child protection or safeguarding investigations
          themselves, or court reports for private family proceedings — those sit under a
          council&apos;s general complaints process instead.
        </p>
      </GuideNote>
    </GuideSection>

    <GuideSection eyebrow="For the writing, not just the facts" title="'Accurate' is not the same as 'written with care'">
      <p>
        If nothing is factually wrong, but the language itself is cold, judgemental, or reduces you
        to a list of incidents, you are still allowed to say so. This is exactly what the ICO&apos;s
        own current work with organisations is pushing them to fix — its research with
        care-experienced people surfaced exactly this kind of thing, in their own words: one person
        put it as{' '}
        <SourceLink href="https://ico.org.uk/about-the-ico/campaigns/better-records-together/our-care-records-standards/providing-care-records/">
          "It was my journey but they chaperoned me,"
        </SourceLink>{' '}
        describing how bureaucratic and clinical the process of reading their own history felt.
        Another said simply,{' '}
        <SourceLink href="https://ico.org.uk/about-the-ico/campaigns/better-records-together/our-care-records-standards/creating-care-records/">
          "They are rightfully mine."
        </SourceLink>{' '}
        A complaint about tone and respect, not accuracy, is a legitimate thing to raise with an
        organisation, and worth doing even when there is no single sentence you can point to as
        factually wrong.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Common questions" title="Questions people ask us most" tinted>
      <div className="space-y-2" data-testid="records-wrong-faq">
        <GuideFaqItem
          q="Can I get the whole record deleted, not just corrected?"
          a="Rarely, for a genuine case record. Councils and other organisations have their own long legal duties to retain care records — usually until you turn 75 — so a general right to erasure is very unlikely to succeed here, even for a record you strongly dislike. Correction, or a recorded note of your dispute, is the realistic goal for most of this material."
        />
        <GuideFaqItem
          q="What if I don't remember what actually happened, only that this doesn't sound like me?"
          a="You don't need certainty to raise it. Say plainly that you don't recognise this account and why — the tone, the framing, the fact nothing like it appears anywhere else in your file — and ask the organisation to explain what it was based on. That's a fair, specific challenge, not something you need first-hand proof to make."
        />
        <GuideFaqItem
          q="Does correcting a record change my Subject Access Request deadline for everything else?"
          a="No — rectification is a separate request from a SAR, with its own one-month clock. Raising a correction doesn't reset or pause a SAR you've already sent for other records."
        />
        <GuideFaqItem
          q="I found something like this, and I'm not okay right now. What do I do?"
          a="Stop reading, and reach out to someone — a friend, a support worker, or a helpline. Become (0800 023 2033, becomecharity.org.uk) supports care leavers specifically; Samaritans (116 123) will listen any time, about anything, no matter how it sounds out loud. Nothing in your file needs a response from you today."
        />
      </div>
    </GuideSection>

    <GuideSection eyebrow="Read more" title="Where this comes from">
      <p>
        The right to rectification and how organisations must handle a challenge is set out in the
        ICO&apos;s{' '}
        <SourceLink href="https://ico.org.uk/for-the-public/your-right-to-get-your-data-corrected/">
          Your right to get your data corrected
        </SourceLink>
        . Its expectations for how care records should be written and provided are in its{' '}
        <SourceLink href="https://ico.org.uk/about-the-ico/campaigns/better-records-together/our-care-records-standards/">
          Better Records Together care records standards
        </SourceLink>
        . The statutory complaints route sits in the Children Act 1989 and the{' '}
        <SourceLink href="https://www.legislation.gov.uk/uksi/2006/1738/contents/made">
          Children Act 1989 Representations Procedure (England) Regulations 2006
        </SourceLink>
        , explained in plain terms by the{' '}
        <SourceLink href="https://www.lgo.org.uk/make-a-complaint/fact-sheets/social-care/social-care-for-children/the-children-act-1989-complaints-procedure">
          Local Government and Social Care Ombudsman
        </SourceLink>
        .
      </p>
    </GuideSection>

    <GuideCTA
      heading="Read at your own pace, with everything in one place"
      body="Precious Data brings every reply into one timeline, flags likely-sensitive content before you open it, and keeps a record of anything you dispute alongside the original — so nothing gets lost, and nothing has to be dealt with before you're ready."
      testIdPrefix="guide-cta-end"
    />
  </GuideLayout>
);

export default RecordsWrongOrUnkind;
